import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  EllipsisVertical,
  MapPin,
  Plus,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Instructor, Location, Venue } from "../../../api/schemas.js";
import { shortZoneName } from "../../../lib/timezone.js";
import { LogoutButton } from "../../../ui/LogoutButton.js";
import type { InstructorPaint } from "../model/map-events.js";
import { instructorInitials } from "../model/map-events.js";
import type { CalendarViewMode, ManagerSurfaceMode } from "../model/types.js";

export interface DealHeaderSummary {
  /** Имя клиента (= имя сделки в amo); id сделки живёт в подсказке, не в тексте. */
  readonly name: string | null;
  /** Короткая сумма долга — всегда видима, даже когда имя обрезано. */
  readonly debtLabel: string | null;
  readonly onOpen: () => void;
}

export interface CalendarToolbarProps {
  readonly mode: ManagerSurfaceMode;
  readonly leadId: number | null;
  readonly view: CalendarViewMode;
  readonly isCompact: boolean;
  readonly isMobile: boolean;
  readonly rangeLabel: string;
  /** Локации каталога 6819: по одной вкладке на остров, календарь у каждого свой. */
  readonly locations: readonly Location[];
  readonly activeLocationId: string | null;
  /**
   * Пояс, в котором стоит сетка. Подписать его обязательно: после того как сетка поехала по
   * часам площадки, «10:00» у менеджера из другого пояса больше не значит его собственное
   * время, и сказать об этом было негде, кроме карточки урока.
   */
  readonly timeZone: string;
  readonly venues: readonly Venue[];
  readonly selectedVenueIds: readonly string[];
  readonly instructors: readonly Instructor[];
  readonly instructorColors: ReadonlyMap<string, InstructorPaint>;
  readonly focusedInstructorId: string | null;
  readonly deal?: DealHeaderSummary;
  readonly onViewChange: (view: CalendarViewMode) => void;
  readonly onLocationChange: (locationId: string) => void;
  readonly onVenueFilterChange: (ids: string[]) => void;
  readonly onCreate: () => void;
  readonly createDisabled?: boolean;
  /** Сколько уроков ждут времени: инструктор передал запись, а назначить её некому, кроме менеджера. */
  readonly unscheduledCount?: number;
  readonly onOpenUnscheduled?: () => void;
  readonly onRefresh: () => void;
  readonly onInstructorFocus: (id: string | null) => void;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onToday: () => void;
}

const VIEWS: Array<{ id: CalendarViewMode; label: string; testId: string }> = [
  { id: "resourceTimeGridDay", label: "День", testId: "view-day" },
  { id: "timeGridWeek", label: "Неделя", testId: "view-week" },
  { id: "dayGridMonth", label: "Месяц", testId: "view-month" },
];

/**
 * Весь хром календаря — одна строка без переносов (правило 4
 * docs/frontend-architecture.md): что не влезло, уходит в меню «⋯».
 * Технические подписи вроде `lead N` живут в подсказке, не в шапке.
 */
export function CalendarToolbar(props: CalendarToolbarProps) {
  const selectedVenues = new Set(props.selectedVenueIds);
  const venueLabel =
    props.selectedVenueIds.length === 0
      ? "Все площадки"
      : props.selectedVenueIds.length === 1
        ? (props.venues.find((v) => v.id === props.selectedVenueIds[0])?.name ?? "Площадка")
        : `Площадки: ${props.selectedVenueIds.length}`;

  const venueMenuItems = (
    <>
      {props.venues.map((venue) => (
        <DropdownMenuCheckboxItem
          key={venue.id}
          checked={selectedVenues.has(venue.id)}
          className="min-h-11"
          data-testid={`venue-option-${venue.id}`}
          onCheckedChange={(checked) => {
            const next = new Set(props.selectedVenueIds);
            if (checked) {
              next.add(venue.id);
            } else {
              next.delete(venue.id);
            }
            props.onVenueFilterChange([...next]);
          }}
        >
          {venue.name}
        </DropdownMenuCheckboxItem>
      ))}
      {props.selectedVenueIds.length > 0 ? (
        <DropdownMenuItem
          className="min-h-11"
          data-testid="venue-filter-clear"
          onClick={() => props.onVenueFilterChange([])}
        >
          Показать все
        </DropdownMenuItem>
      ) : null}
    </>
  );

  return (
    <TooltipProvider>
      <div
        className="flex min-w-0 flex-nowrap items-center gap-1.5 px-2 py-2 sm:gap-2 sm:px-4"
        data-testid="manager-toolbar"
      >
        {/* Режим поверхности нужен только скринридеру: видимый бейдж ломал шапку. */}
        <span className="sr-only" data-testid="surface-mode">
          {props.mode === "deal" ? `Сделка · lead ${props.leadId}` : "Глобально"}
        </span>

        {/*
         * Локация — не фильтр, а выбор календаря: у Бали и Пхукета своё время, свои площадки и
         * свои инструкторы, и одна сетка на оба острова читалась неправильно всегда. В карточке
         * amo выбирать нечего — сделка уже принадлежит острову, и он подписан рядом.
         */}
        {props.mode === "global" && props.locations.length > 1 ? (
          <ToggleGroup
            type="single"
            value={props.activeLocationId ?? ""}
            onValueChange={(value) => {
              if (value) props.onLocationChange(value);
            }}
            className="shrink-0 rounded-full bg-muted p-0.5"
            data-testid="calendar-location-switch"
            aria-label="Локация календаря"
            title={`Сетка во времени площадки: ${props.timeZone}`}
          >
            {props.locations.map((location) => (
              <ToggleGroupItem
                key={location.id}
                value={location.id}
                data-testid={`calendar-location-${location.id}`}
                className="min-h-10 rounded-full px-3"
              >
                {location.name}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        ) : props.activeLocationId ? (
          // В карточке amo остров выбирать нечего, но знать его надо: сетка стоит в его поясе.
          <span
            className="pwz-readout shrink-0 rounded-full bg-muted px-3 py-1 text-ink-700 text-xs"
            data-testid="calendar-location-label"
            title={`Сетка во времени площадки: ${props.timeZone}`}
          >
            {props.locations.find((location) => location.id === props.activeLocationId)?.name ?? ""}
          </span>
        ) : null}

        {/* Пояс сетки — не отдельное значение в шапке (их там было и так слишком много), а
            подсказка к локации: сетка стоит во времени острова, который выбран слева. */}
        <span className="sr-only" data-testid="calendar-timezone">
          Время площадки: {shortZoneName(props.timeZone)}
        </span>

        {props.isMobile ? null : (
          <ToggleGroup
            type="single"
            value={props.view}
            onValueChange={(value) => {
              if (value) props.onViewChange(value as CalendarViewMode);
            }}
            className="shrink-0 rounded-full bg-muted p-0.5"
            aria-label="Режим календаря"
          >
            {VIEWS.map((v) => (
              <ToggleGroupItem
                key={v.id}
                value={v.id}
                data-testid={v.testId}
                className="min-h-10 rounded-full px-3.5 data-[state=on]:bg-ink-950 data-[state=on]:text-white"
              >
                {v.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}

        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11"
            aria-label="Предыдущий период"
            data-testid="range-prev"
            onClick={props.onPrev}
          >
            <ChevronLeft />
          </Button>
          {props.isCompact ? null : (
            <Button
              type="button"
              variant="outline"
              className="h-11"
              data-testid="range-today"
              onClick={props.onToday}
            >
              Сегодня
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11"
            aria-label="Следующий период"
            data-testid="range-next"
            onClick={props.onNext}
          >
            <ChevronRight />
          </Button>
        </div>

        <h1
          className="min-w-0 flex-1 truncate font-extrabold text-base tracking-tight"
          data-testid="range-label"
        >
          {props.rangeLabel}
        </h1>

        {props.deal ? (
          <Button
            type="button"
            variant="outline"
            className="h-11 min-w-0 max-w-44 shrink sm:max-w-64"
            title={`Сделка · lead ${props.leadId}`}
            data-testid="deal-drawer-button"
            onClick={props.deal.onOpen}
          >
            <Wallet className="shrink-0" />
            {props.deal.name ? <span className="min-w-0 truncate">{props.deal.name}</span> : null}
            {props.deal.debtLabel ? (
              <span className="pwz-readout shrink-0 text-xs">{props.deal.debtLabel}</span>
            ) : null}
          </Button>
        ) : null}

        {/* В карточке сделки фильтровать площадки нечего: на экране одна сделка. */}
        {props.isCompact || props.mode === "deal" ? null : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-11 max-w-52"
                data-testid="venue-filter"
              >
                <MapPin className="shrink-0" />
                <span className="min-w-0 truncate">{venueLabel}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Площадки</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {venueMenuItems}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {!props.isCompact && props.mode === "global" && props.instructors.length > 0 ? (
          <fieldset className="flex shrink-0 items-center" aria-label="Фильтр по инструктору">
            {props.instructors.slice(0, 6).map((instructor, index) => {
              const active = props.focusedInstructorId === instructor.id;
              const dimmed = props.focusedInstructorId !== null && !active;
              return (
                <Tooltip key={instructor.id}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-pressed={active}
                      data-testid={`instructor-filter-${instructor.id}`}
                      className={cn(
                        "size-11 rounded-full border-2 border-white font-bold text-[11px] text-white transition-opacity hover:text-white",
                        index > 0 && "-ml-3",
                        dimmed ? "opacity-40" : "opacity-100",
                        active && "ring-2 ring-primary ring-offset-1",
                      )}
                      style={{
                        backgroundColor:
                          props.instructorColors.get(instructor.id)?.stripe ??
                          "var(--color-ink-500)",
                      }}
                      onClick={() => props.onInstructorFocus(active ? null : instructor.id)}
                    >
                      {instructorInitials(instructor.name)}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {active ? `${instructor.name} — показать всех` : instructor.name}
                  </TooltipContent>
                </Tooltip>
              );
            })}
            {props.instructors.length > 6 ? (
              // Раньше это была неподвижная подпись, и седьмого инструктора отфильтровать было
              // нельзя вовсе. Теперь остальные живут в меню.
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Ещё инструкторов: ${props.instructors.length - 6}`}
                    data-testid="instructor-filter-more"
                    className="-ml-3 size-11 rounded-full border-2 border-white bg-muted font-bold text-[11px] text-muted-foreground hover:text-muted-foreground"
                  >
                    +{props.instructors.length - 6}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Инструкторы</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {props.instructors.slice(6).map((instructor) => (
                    <DropdownMenuCheckboxItem
                      key={instructor.id}
                      className="min-h-11"
                      checked={props.focusedInstructorId === instructor.id}
                      data-testid={`instructor-filter-${instructor.id}`}
                      onCheckedChange={(checked) =>
                        props.onInstructorFocus(checked ? instructor.id : null)
                      }
                    >
                      {instructor.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                  {props.focusedInstructorId !== null ? (
                    <DropdownMenuItem
                      className="min-h-11"
                      data-testid="instructor-filter-clear"
                      onClick={() => props.onInstructorFocus(null)}
                    >
                      Показать всех
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </fieldset>
        ) : null}

        {/* Служебное — обновить, админка, выход — живёт в одном меню на любой ширине: три иконки
            подряд в шапке были ещё тремя значениями, которые менеджер читал каждый раз. */}
        {
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="size-11"
                aria-label="Ещё"
                data-testid="toolbar-more"
              >
                <EllipsisVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              {props.isCompact ? (
                <DropdownMenuItem
                  className="min-h-11"
                  data-testid="range-today"
                  onClick={props.onToday}
                >
                  Сегодня
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                className="min-h-11"
                data-testid="refresh-calendar"
                onClick={props.onRefresh}
              >
                Обновить
              </DropdownMenuItem>
              <DropdownMenuItem className="min-h-11" asChild data-testid="link-admin">
                <a href="/admin">Админ-панель</a>
              </DropdownMenuItem>
              <DropdownMenuItem className="min-h-11 p-0" asChild>
                <LogoutButton variant="menu" />
              </DropdownMenuItem>
              {props.isCompact && props.mode === "global" ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Площадки</DropdownMenuLabel>
                  {venueMenuItems}
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        }

        {props.mode === "global" &&
        props.unscheduledCount !== undefined &&
        props.unscheduledCount > 0 &&
        props.onOpenUnscheduled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size={props.isCompact ? "icon" : "default"}
                className={cn("shrink-0", props.isCompact ? "size-11" : "h-11")}
                aria-label={`Уроков без времени: ${props.unscheduledCount}`}
                data-testid="unscheduled-badge"
                onClick={props.onOpenUnscheduled}
              >
                <CalendarClock />
                {props.isCompact ? null : (
                  <span className="ml-1 tabular-nums">{props.unscheduledCount}</span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Уроков без времени: {props.unscheduledCount}. Инструктор мог передать запись вам.
            </TooltipContent>
          </Tooltip>
        ) : null}

        <Button
          type="button"
          size={props.isCompact ? "icon" : "default"}
          className={cn("shrink-0", props.isCompact ? "size-11" : "h-11")}
          aria-label={props.isCompact ? "Записать урок" : undefined}
          data-testid="create-lesson"
          disabled={props.createDisabled}
          onClick={props.onCreate}
        >
          {props.isCompact ? <Plus /> : "Записать урок"}
        </Button>
      </div>
    </TooltipProvider>
  );
}
